import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { ERRORS, PATH } from '../../../constants';
import { sortByConfidenceDesc } from '../../../utils';
import { ReactComponent as Icon } from '../../../img/readmore-arrow.svg';

import Loader from '../Loader/Loader';
import useOutsideClick from '../../Hooks/useOutsideClick';

const Autocomplete = ({
    parentRef,
    results,
    isFetchingData,
    isVisible,
    searchConfidence,
    searchPhrase,
    triggerAutocompleteHiding,
    submitHandler,
    error
}) => {
    const { t } = useTranslation();
    const { clickedOutside } = useOutsideClick(parentRef);
    const [shouldRenderContent, setShouldRenderContent] = useState(false);

    useEffect(() => {
        setShouldRenderContent(false);
    }, [triggerAutocompleteHiding]);

    useEffect(() => {
        if (isVisible && !clickedOutside) {
            setShouldRenderContent(true);
        }

        return () => {
            setShouldRenderContent(false);
        };
    }, [isVisible, clickedOutside]);

    const areResultsPresent = () => results && results.length !== 0;

    const loader = (
        <div className="autocomplete__loader" data-cy="search-bar-loader">
            <Loader spinner />
        </div>
    );

    const getList = () => {
        if (error === ERRORS.unhandled) {
            return (
                <li className="autocomplete__item autocomplete__item--msg">
                    {t('ui.autocomplete.error')}
                </li>
            );
        }

        if (!areResultsPresent()) {
            return (
                <li
                    className="autocomplete__item autocomplete__item--msg"
                    data-cy="autocomplete-nothing-found"
                >
                    {t('ui.autocomplete.empty')} &quot;{searchPhrase}&quot;
                </li>
            );
        }

        const sortedResults = sortByConfidenceDesc(results);
        return sortedResults.map((item) => (
            <li className="autocomplete__item" key={item.id}>
                <Link
                    state={{
                        searchPhrase: searchPhrase,
                        searchConfidence: searchConfidence
                    }}
                    className="autocomplete__link"
                    data-cy="autocomplete-link"
                    to={`${PATH.helpAndSupport}/${item.categoryId}/${item.id}`}
                >
                    {item.name}
                    <Icon className="autocomplete__icon icon icon--xxs" aria-hidden="true" focusable="false" />
                </Link>
            </li>
        ));
    };

    const content = isFetchingData ? (
        loader
    ) : (
        <>
            <ul className="autocomplete__list" data-cy="autocomplete-list">
                {getList()}
            </ul>
            {areResultsPresent() && (
                <button
                    onClick={submitHandler}
                    className="autocomplete__link autocomplete__link--all"
                    data-cy="autocomplete-link"
                >
                    {t('ui.autocomplete.viewAll')}
                    <Icon className="icon icon--xxs" aria-hidden="true" focusable="false" />
                </button>
            )}
        </>
    );

    return shouldRenderContent && <div className="autocomplete">{content}</div>;
};

export default Autocomplete;
